import {
  type BoxGroup,
  BoxPlot,
  FormatType,
  getOtherColor,
  getPrimaryColor,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { BreakdownBoxDataSet } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import {
  isParameterSelectionEqualToEntity,
  type ParameterSelection,
} from './TextChart/utils/resolve-entity-utils';
import { getActiveColor, useActiveColors } from './utils/useActiveColors';

type BoxProps = {
  data: BreakdownBoxDataSet['data'];
  primaryParameterValues?: ParameterSelection[];
};

export const Box = ({ data, primaryParameterValues }: BoxProps) => {
  const { item } = useChart();
  const colorPalette = item?.plot?.colorPalette;
  const { code } = useCurrencyPreference();
  const { colorMap } = useActiveColors();

  const boxData: BoxGroup[] = data.flatMap((primaryD) =>
    primaryD.breakdown.map((secondaryD): BoxGroup => {
      const dimension = secondaryD.dim;
      const entityRcid = secondaryD.id ?? null;
      const isPrimary =
        primaryParameterValues?.some((p) =>
          isParameterSelectionEqualToEntity(p, dimension, entityRcid)
        ) ?? false;
      const activeColor = getActiveColor({
        colorMap,
        dimension,
        id: entityRcid,
      });
      const fallbackColor = (() => {
        if (!primaryParameterValues?.length) return undefined;
        return isPrimary ? getPrimaryColor() : getOtherColor();
      })();
      const color = activeColor ?? fallbackColor;

      return {
        dimension: primaryD.dim,
        secondaryDimension: secondaryD.dim,
        boxData: {
          percentile10: secondaryD.box.q10,
          ...(secondaryD.box.q25 != null && {
            percentile25: secondaryD.box.q25,
          }),
          percentile50: secondaryD.box.q50,
          ...(secondaryD.box.q75 != null && {
            percentile75: secondaryD.box.q75,
          }),
          percentile90: secondaryD.box.q90,
        },
        ...(color && { color }),
      };
    })
  );

  return (
    <SharableChart
      Component={BoxPlot}
      data={boxData}
      format={FormatType.CURRENCY}
      currencyCode={code}
      colorPalette={colorPalette}
      showQuartiles={item?.plot?.showQuartiles ?? false}
    />
  );
};
